import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './TermsAndConditions.css';

function TermsAndConditions() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  // Check if user has scrolled to the bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrolledToBottom =
      container.scrollHeight - container.scrollTop <= container.clientHeight + 50;

    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [hasScrolledToBottom]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setError('You must agree to the terms and conditions to continue');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const idToken = auth.user.id_token;
      await apiService.acceptTerms(idToken);

      // Redirect to home after successful acceptance
      navigate('/');
      window.location.reload(); // Reload to update user context
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div className="terms-page-container">
      <div className="terms-page-content">
        <h1 className="terms-page-title">Terms and Conditions</h1>
        <p className="terms-page-subtitle">
          Please read and accept our terms and conditions to continue using CoinedIn
        </p>

        <div className="terms-scroll-container" ref={scrollContainerRef}>
          <div className="terms-content">
            <p>
              Our mission is to help people trust and verify their work. These Terms of Service set out the rules for your use of CoinedIn and all related websites, features, applications, services, technologies, and software we provide. These Products are offered by CoinedIn LLC, and these Terms form a binding agreement between you and CoinedIn LLC. If you do not agree to these Terms, you must not access or use CoinedIn or any other products or services governed by them.
            </p>

            <h2>1. Services We Provide</h2>
            <p>
              To support our mission, we offer the following Products and services to you:
            </p>

            <h3>1.1 Connecting You With People and Organizations</h3>
            <p>
              We help you discover and connect with relevant people, groups, businesses, organizations, and others important to your work verification.
            </p>

            <h3>1.2 Enabling Expression and Communication About Your Work</h3>
            <p>
              CoinedIn allows you to express yourself by sharing and communicating your work certifications through our peer recognition platform. Users can send and receive virtual coins as a form of appreciation and recognition within their organization, including both individual and organizational recognition features.
            </p>

            <h3>1.3 Research and Service Improvement</h3>
            <p>
              We conduct research to develop, test, and enhance our Products. This includes analyzing user data to better understand how our Products are used, as well as conducting surveys, testing new features, and troubleshooting to improve functionality and performance.
            </p>

            <h3>1.4 Ensuring Access to Our Services</h3>
            <p>
              To operate our global services and enable connections worldwide, we transfer, store, and distribute content and data across our data centers, partners, service providers, vendors, and systems, including those located outside your country of residence. This global infrastructure is necessary and essential to deliver our services.
            </p>

            <h2>2. Your Commitments to CoinedIn</h2>
            <p>
              In exchange for using our services, you agree to the following commitments:
            </p>
            <ul>
              <li>You must be at least 13 years old (or the minimum age required in your jurisdiction) to use the Service</li>
              <li>Provide accurate and truthful information about yourself</li>
              <li>Use the same name on your account that you use in everyday life</li>
              <li>Keep your password secure and do not share it, grant others access to your CoinedIn account, or transfer your account to anyone else without our permission</li>
              <li>Maintain only one account—your own—and use it solely for personal purposes</li>
              <li>Maintain and promptly update your account information</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3>2.1 Prohibited Activities</h3>
            <p>You agree not to engage in any of the following prohibited activities:</p>
            <ul>
              <li>Upload viruses or malicious code, use the services to send spam, or engage in any activity that could disrupt, overload, interfere with, or damage the functionality, integrity, operation, or appearance of our services, systems, or Products</li>
              <li>Access or collect data from our Products through automated means without our prior permission, nor may you attempt to access data that you are not authorized to access</li>
              <li>Use proxies, request, or collect Product usernames or passwords, nor may you misappropriate access tokens</li>
              <li>Sell, license, or buy any data obtained from us or through our services</li>
              <li>Abuse any reporting, flagging, dispute, or appeals system by submitting fraudulent, duplicate, or baseless reports or appeals</li>
              <li>Take, or attempt to take, any action to circumvent, bypass, or override any technological measures CoinedIn uses to control or restrict access to our Products or data</li>
              <li>Use the Service in any way that violates applicable laws or regulations</li>
              <li>Send spam, harassment, or offensive content through the Service</li>
              <li>Impersonate another person or organization</li>
              <li>Attempt to gain unauthorized access to the Service or other user accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
            </ul>
            <p>
              We may remove or limit access to content that violates these provisions and may suspend or disable your account for conduct that breaches these rules.
            </p>
            <p>
              To help maintain a safe and fair community, we encourage you to report any content or behavior that you believe infringes on your rights or violates our terms and policies.
            </p>

            <h3>2.2 Permission to Use Content You Create and Share</h3>
            <p>
              You retain ownership of any content you submit through the Service, including coin messages and descriptions. However, you grant us a non-exclusive, transferable, sub-licensable, royalty-free, worldwide license to host, use, distribute, modify, run, copy, publicly perform or display, translate, and create derivative works of any content you create or share.
            </p>
            <p>
              You are solely responsible for the content you post and agree that your content will not:
            </p>
            <ul>
              <li>Violate any third-party rights, including copyrights and privacy rights</li>
              <li>Contain hate speech, threats, or discriminatory content</li>
              <li>Contain false or misleading information</li>
              <li>Violate any laws or regulations</li>
            </ul>

            <h3>2.3 Permission to Use Your Name, Profile Picture, and Activity</h3>
            <p>
              You grant us the right to use your name, profile picture, and information about your actions on CoinedIn in connection with ads, offers, or other sponsored or commercial content displayed across our Products, without any obligation to provide compensation to you.
            </p>

            <h3>2.4 Permission to Update Software</h3>
            <p>
              If you download or use our software, you give us permission to download and install updates to the software where available.
            </p>

            <h2>3. Virtual Coins</h2>
            <p>
              Virtual coins provided through the Service have no monetary value and cannot be exchanged for cash or any other form of compensation. Coins are solely for recognition purposes within the platform. We reserve the right to modify coin balances or features at any time.
            </p>

            <h2>4. Privacy and Data Protection</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy. We collect and use your personal information as described in our Privacy Policy, including your name, email address, and usage data. By using the Service, you consent to our collection and use of personal information in accordance with our Privacy Policy.
            </p>

            <h2>5. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by CoinedIn and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our Service without our prior written consent.
            </p>

            <h2>6. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account and access to the Service at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
            </p>
            <p>
              Upon termination, your right to use the Service will immediately cease. You may also terminate your account at any time by contacting us or through your account settings.
            </p>

            <h2>7. Disclaimers and Limitations of Liability</h2>

            <h3>7.1 Service Disclaimer</h3>
            <p>
              We strive to deliver the highest-quality Products and to establish clear guidelines for their use. However, our Products are provided "as is," and we do not guarantee that they will always be safe, secure, error-free, or operate without interruptions, delays, or imperfections. To the extent permitted by law, we also DISCLAIM ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not control or direct what people and others do or say, and we are not responsible for their actions or conduct (whether online or offline) or any content they share (including offensive, inappropriate, obscene, unlawful, and other objectionable content).
            </p>
            <p>
              We do not warrant that:
            </p>
            <ul>
              <li>The Service will be uninterrupted, secure, or error-free</li>
              <li>The results obtained from using the Service will be accurate or reliable</li>
              <li>Any errors in the Service will be corrected</li>
            </ul>

            <h3>7.2 Limitation of Liability</h3>
            <p>
              We cannot anticipate when issues may arise with our Products. To the fullest extent permitted by applicable law, our liability is limited, and under no circumstances will we be liable for any lost profits, revenues, information, or data, or for any consequential, special, indirect, exemplary, punitive, or incidental damages arising out of or related to these Terms or the Products, regardless of the cause or theory of liability, including negligence, even if we have been advised of the possibility of such damages.
            </p>
            <p>
              Our total liability arising out of or relating to these Terms or the Products will not exceed the greater of $100 or the amount you have paid us in the preceding twelve months.
            </p>

            <h2>8. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless CoinedIn, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with your access to or use of the Service, your violation of these Terms, or your violation of any rights of another party.
            </p>

            <h2>9. Updates and Changes to Terms</h2>
            <p>
              We continually work to improve our services and develop new features for the benefit of you and our community. As a result, we may update these Terms from time to time to reflect our services and practices accurately, promote a safe and secure experience, and comply with applicable law. Unless otherwise required by law, we will notify you before any changes take effect and provide you with an opportunity to review the updated Terms. By continuing to access or use our Products after the updated Terms become effective, you agree to be bound by them.
            </p>

            <h2>10. Additional Provisions</h2>

            <h3>10.1 Severability</h3>
            <p>
              If any provision of these Terms is found to be unenforceable, that provision will be modified to the minimum extent necessary to make it enforceable, or, if modification is not possible, severed from the Terms. The remaining provisions will continue in full force and effect. Our failure to enforce any provision of these Terms does not constitute a waiver. Any amendment to or waiver of these Terms must be in writing and signed by us.
            </p>

            <h3>10.2 Assignment and Transfer</h3>
            <p>
              You may not transfer or assign any of your rights or obligations under these Terms to another party without our prior consent. Our rights and obligations under these Terms may be freely assigned by us, including in connection with a merger, acquisition, sale of assets, or by operation of law or otherwise.
            </p>

            <h3>10.3 Third-Party Beneficiaries</h3>
            <p>
              These Terms do not grant any rights to third-party beneficiaries.
            </p>

            <h3>10.4 Legacy Contact</h3>
            <p>
              You may appoint a person, known as a legacy contact, to manage your account if it is memorialized.
            </p>

            <h3>10.5 Username Changes</h3>
            <p>
              In certain circumstances, we may need to change your account username—for example, if another party claims the username and it does not appear to be related to the name you commonly use in everyday life.
            </p>

            <h3>10.6 Feedback</h3>
            <p>
              We welcome and value your feedback and suggestions regarding our products and services. However, any feedback or suggestions you provide may be used by us without restriction, without obligation to compensate you, and without any duty to keep such feedback confidential.
            </p>

            <h2>11. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which CoinedIn operates, without regard to its conflict of law provisions.
            </p>
            <p>
              Any disputes arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except where prohibited by law.
            </p>

            <h2>12. Entire Agreement</h2>
            <p>
              These Terms constitute the entire agreement between you and CoinedIn regarding the use of the Service and supersede all prior agreements and understandings, whether written or oral.
            </p>

            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at adamhb321@gmail.com or h2bedi@gmail.com.
            </p>

            <p className="terms-last-updated">
              <strong>Last Updated:</strong> December 18, 2025
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="terms-acceptance-form">
          {hasScrolledToBottom && (
            <div className="terms-checkbox-container">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  disabled={submitting}
                  className="terms-checkbox"
                />
                <span>I have read and agree to the Terms and Conditions</span>
              </label>
            </div>
          )}

          {!hasScrolledToBottom && (
            <p className="terms-scroll-instruction">
              Please scroll to the bottom to accept the terms
            </p>
          )}

          {error && (
            <div className="terms-error-message">
              {error}
            </div>
          )}

          {hasScrolledToBottom && (
            <button
              type="submit"
              disabled={!agreed || submitting}
              className="terms-accept-button"
            >
              {submitting ? <LoadingSpinner message="" /> : 'Accept and Continue'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default TermsAndConditions;
